<script setup>
import { useAlert } from 'dashboard/composables';
import { useRouter } from 'vue-router';
import AddKnowledgebase from './AddKnowledgebase.vue';
import KnowledgebasesTable from './KnowledgebasesTable.vue';
import WootConfirmDeleteModal from 'dashboard/components/widgets/modal/ConfirmDeleteModal.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import KnowledgebaseLayout from 'dashboard/components-next/Knowledgebase/KnowledgebaseLayout.vue';
import { computed, onMounted, ref, defineOptions } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';
import { useToggle } from '@vueuse/core';

defineOptions({
  name: 'KnowledgebaseSettings',
});

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const showAddPopup = ref(false);
const currentKnowledgebase = ref({});
const loading = ref({});
const knowledgebaseToDelete = ref(null);
const [showDeletePopup, toggleDeletePopup] = useToggle();
const initialLoading = ref(true);

const knowledgebases = useMapGetter('knowledgebase/getLibraries');
const uiFlags = useMapGetter('knowledgebase/getUIFlags');

const [showKnowledgebaseDialog, toggleKnowledgebaseDialog] = useToggle();

const confirmPlaceHolderText = computed(() => {
  if (!knowledgebaseToDelete.value) return '';
  return t('LIBRARIES.DELETE.CONFIRM.PLACE_HOLDER', {
    knowledgebaseName: knowledgebaseToDelete.value.name,
  });
});

const fetchKnowledgebases = async () => {
  try {
    await store.dispatch('knowledgebase/get');
  } catch (error) {
    useAlert(error?.message || t('LIBRARIES.FAIL_FETCH_MESSAGE'));
  } finally {
    initialLoading.value = false;
  }
};

onMounted(() => {
  fetchKnowledgebases();
});

const showAlertMessage = message => {
  loading[currentKnowledgebase.value.id] = false;
  currentKnowledgebase.value = {};
  useAlert(message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openKnowledgebase = knowledgebase => {
  router.push({
    name: 'settings_knowledgebases_content',
    params: { knowledgebaseId: knowledgebase.id },
  });
};

const confirmDeleteKnowledgebase = knowledgebase => {
  knowledgebaseToDelete.value = knowledgebase;
  toggleDeletePopup(true);
};

const deleteKnowledgebase = async () => {
  toggleDeletePopup(false);
  if (!knowledgebaseToDelete.value) return;

  try {
    await store.dispatch(
      'knowledgebase/delete',
      knowledgebaseToDelete.value.id
    );
    useAlert(t('LIBRARIES.DELETE.API.SUCCESS_MESSAGE'));
    knowledgebaseToDelete.value = null;
  } catch (error) {
    useAlert(error?.message || t('LIBRARIES.DELETE.API.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <KnowledgebaseLayout
    :header-title="$t('LIBRARIES.HEADER')"
    :button-label="$t('LIBRARIES.HEADER_BTN_TXT.CREATE')"
    @click="toggleKnowledgebaseDialog()"
    @close="toggleKnowledgebaseDialog(false)"
  >
    <div class="mt-6 flex-1">
      <!-- Loading State -->
      <div
        v-if="initialLoading || uiFlags.isFetching"
        class="flex items-center justify-center h-64"
      >
        <div class="flex flex-col items-center">
          <div
            class="w-8 h-8 border-4 border-woot-500 border-t-transparent rounded-full animate-spin mb-3"
          />
          <p class="text-sm text-slate-500">
            {{ $t('LIBRARIES.LOADING') }}
          </p>
        </div>
      </div>

      <!-- Empty State -->
      <p
        v-else-if="!knowledgebases.length"
        class="flex flex-col items-center justify-center h-full text-base text-slate-600 dark:text-slate-300 py-8"
      >
        {{ $t('LIBRARIES.LIST.404') }}
      </p>

      <!-- Knowledgebases Table -->
      <KnowledgebasesTable
        v-else
        :knowledgebases="knowledgebases"
        :is-loading="uiFlags.isFetching"
        @select="openKnowledgebase"
        @edit="openKnowledgebase"
        @delete="confirmDeleteKnowledgebase"
      />
    </div>

    <template #action>
      <AddKnowledgebase
        v-show="showKnowledgebaseDialog"
        :active="showKnowledgebaseDialog"
        @close="toggleKnowledgebaseDialog(false)"
      />
    </template>
  </KnowledgebaseLayout>

  <WootConfirmDeleteModal
    v-if="showDeletePopup && knowledgebaseToDelete"
    v-model:show="showDeletePopup"
    :title="$t('LIBRARIES.DELETE.CONFIRM.TITLE')"
    :message="$t('LIBRARIES.DELETE.CONFIRM.MESSAGE')"
    :confirm-text="$t('LIBRARIES.DELETE.CONFIRM.YES')"
    :reject-text="$t('LIBRARIES.DELETE.CONFIRM.REJECT')"
    :confirm-value="knowledgebaseToDelete.name"
    :confirm-place-holder-text="confirmPlaceHolderText"
    @on-confirm="deleteKnowledgebase"
    @on-close="toggleDeletePopup(false)"
  />
</template>
